import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { useNavigate, Link } from "react-router-dom";
import {
  RiCalendarLine,
  RiInboxLine,
  RiSearchLine,
} from "@remixicon/react";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import api from "@/utils/api";
import type { Order, BulkOrderAction } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";
import { statusBadgeClass } from "@/lib/status";

const orderStatuses = [
  { label: "All Statuses", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Processing", value: "processing" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Refunded", value: "refunded" },
];

const paymentStatuses = [
  { label: "All Payments", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Cancelled", value: "cancelled" },
];

export function OrderList() {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    paymentStatus: "all",
    search: "",
    page: 1,
    limit: 20,
  });

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { storeId } = useCurrentStore();
  const [isMobile, setIsMobile] = useState(false);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page: 1,
      }));
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Build query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all") {
        params.append(key, value.toString());
      }
    });

    if (dateRange.from) {
      params.append("startDate", dateRange.from.toISOString());
    }
    if (dateRange.to) {
      params.append("endDate", dateRange.to.toISOString());
    }

    return params.toString();
  }, [filters, dateRange]);

  // Fetch orders
  const { data: ordersData, isLoading } = useQuery({
    queryKey: ["orders", queryParams, storeId],
    queryFn: async () => {
      const { data } = await api.get(`/api/orders?${queryParams}`, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
    enabled: !!storeId && !!queryParams,
  });

  // Bulk action mutation
  const bulkMutation = useMutation({
    mutationFn: async (actionData: BulkOrderAction) => {
      const { data } = await api.post("/api/orders/bulk", actionData, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      setSelectedOrders([]);
    },
  });

  const handleBulkAction = (
    action: BulkOrderAction["action"],
    additionalData?: any
  ) => {
    if (selectedOrders.length === 0) return;

    bulkMutation.mutate({
      action,
      orderIds: selectedOrders,
      ...additionalData,
    });
  };

  const getStatusColor = statusBadgeClass;
  const getPaymentStatusColor = statusBadgeClass;

  const formatCurrency = (amount: number, currency = "NGN") => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const orders = ordersData?.orders || [];
  const pagination = ordersData?.pagination || {};

  return (
    <div className="space-y-6">
      <SectionCard className="p-0 overflow-hidden">
        <div className="p-4 pb-0">
          {/* Filter Area - Always Visible */}
          <div className="flex flex-wrap items-center gap-4 mb-4 w-full">
            <div className="relative flex-1 w-full">
              <RiSearchLine className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders, customers..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={dashboardStyles.filterInput}
              />
            </div>

            <Select
              value={filters.status}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  status: value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-[150px]`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {orderStatuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.paymentStatus}
              onValueChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  paymentStatus: value,
                  page: 1,
                }))
              }
            >
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-[150px]`}>
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                {paymentStatuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`${dashboardStyles.filterSelect} w-[240px] justify-start bg-white text-left font-normal`}
                >
                  <RiCalendarLine className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "LLL dd, y")} -{" "}
                        {format(dateRange.to, "LLL dd, y")}
                      </>
                    ) : (
                      format(dateRange.from, "LLL dd, y")
                    )
                  ) : (
                    <span>Pick date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange.from}
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || {});
                    setFilters((prev) => ({ ...prev, page: 1 }));
                  }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>

            {(filters.search ||
              filters.status !== "all" ||
              filters.paymentStatus !== "all" ||
              dateRange.from) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters({
                    status: "all",
                    paymentStatus: "all",
                    search: "",
                    page: 1,
                    limit: 20,
                  });
                  setSearchInput("");
                  setDateRange({});
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Bulk Actions */}
          {selectedOrders.length > 0 && (
            <div className="mx-4 mb-4 rounded-2xl border border-[#EBEBEB] bg-[#F5F5F3] p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {selectedOrders.length} order(s) selected
                  </span>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          Bulk Actions
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          onClick={() =>
                            handleBulkAction("updateStatus", {
                              status: "confirmed",
                            })
                          }
                        >
                          Mark as Confirmed
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleBulkAction("updateStatus", {
                              status: "processing",
                            })
                          }
                        >
                          Mark as Processing
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleBulkAction("updateStatus", {
                              status: "shipped",
                            })
                          }
                        >
                          Mark as Shipped
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleBulkAction("updateStatus", {
                              status: "delivered",
                            })
                          }
                        >
                          Mark as Delivered
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleBulkAction("export")}
                        >
                          Export Selected
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedOrders([])}
                    >
                      Clear selection
                    </Button>
                  </div>
                </div>
            </div>
          )}

          {/* Orders Display Section - Shows Loading or Content */}
          {isLoading ? (
            isMobile ? (
              // Mobile loading skeleton
              <div className="space-y-3 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className={dashboardStyles.listItem}>
                    <div className="w-full">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-32 mb-1.5" />
                          <Skeleton className="h-3 w-44" />
                        </div>
                        <Skeleton className="h-6 w-14 rounded-full" />
                      </div>
                      <div className="flex gap-2 mb-2">
                        <Skeleton className="h-5 w-20 rounded-full" />
                        <Skeleton className="h-5 w-24 rounded-full" />
                      </div>
                      <div className="flex items-center justify-between">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Desktop loading skeleton
              <div className={`${dashboardStyles.tableWrap} m-4`}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead className="w-8"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-4 rounded" /></TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24 mb-1" />
                          <Skeleton className="h-3 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-28 mb-1" />
                          <Skeleton className="h-3 w-40" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24 mb-1" />
                          <Skeleton className="h-3 w-16" />
                        </TableCell>
                        <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                        <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            // Actual orders content
            <>
              {isMobile ? (
                /* Mobile Card View */
                <div className="space-y-3 p-4">
                  {orders.map((order: Order) => (
                    <OrderCard
                      key={order._id}
                      order={order}
                      onView={(orderId) =>
                        navigate(`/dashboard/orders/${orderId}`)
                      }
                      getStatusColor={getStatusColor}
                      getPaymentStatusColor={getPaymentStatusColor}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>
              ) : (
                /* Desktop Table View */
                <div className={`${dashboardStyles.tableWrap} m-4`}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">
                          <Checkbox
                            checked={
                              selectedOrders.length === orders.length &&
                              orders.length > 0
                            }
                            onCheckedChange={(checked) => {
                              setSelectedOrders(
                                checked ? orders.map((o: Order) => o._id) : []
                              );
                            }}
                          />
                        </TableHead>
                        <TableHead>Order</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead className="w-8"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order: Order) => (
                        <TableRow key={order._id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedOrders.includes(order._id)}
                              onCheckedChange={(checked) => {
                                setSelectedOrders((ids) =>
                                  checked
                                    ? [...ids, order._id]
                                    : ids.filter((id) => id !== order._id)
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {order.orderNumber}
                              </div>
                              <div className="text-sm text-muted-foreground capitalize">
                                {order.source}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">
                                {order.customer.name}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {order.customer.email}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {format(new Date(order.createdAt), "MMM d, yyyy")}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(order.createdAt), "h:mm a")}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(order.status)}>
                              {order.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={getPaymentStatusColor(
                                order.payment.status
                              )}
                            >
                              {order.payment.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {order.items.length} item
                              {order.items.length !== 1 ? "s" : ""}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {formatCurrency(
                                order.pricing.total,
                                order.pricing.currency
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/dashboard/orders/${order._id}`}
                              className="text-primary hover:underline"
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {orders.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 px-4 mx-4 mb-4 rounded-xl bg-muted/20">
                  <div className={dashboardStyles.emptyIcon}>
                    <RiInboxLine className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className={dashboardStyles.emptyTitle}>
                    {filters.search || filters.status !== "all" || dateRange.from
                      ? "No orders found"
                      : "No orders yet"}
                  </p>
                  <p className={dashboardStyles.emptyDescription}>
                    {filters.search || filters.status !== "all" || dateRange.from
                      ? "No orders match your current filters. Try adjusting or clearing them."
                      : "Orders from your customers will appear here once they start purchasing."}
                  </p>
                  {(filters.search || filters.status !== "all" || dateRange.from) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilters({ status: "all", paymentStatus: "all", search: "", page: 1, limit: 20 });
                        setSearchInput("");
                        setDateRange({});
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </SectionCard>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className={dashboardStyles.paginationBar}>
          <div>
            Showing {(pagination.currentPage - 1) * pagination.limit + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.limit,
              pagination.total
            )}{" "}
            of {pagination.total} orders
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              disabled={pagination.currentPage <= 1}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  page: Math.max(1, prev.page - 1),
                }))
              }
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const page = i + 1;
                return (
                  <Button
                    key={page}
                    variant={
                      page === pagination.currentPage ? "default" : "outline"
                    }
                    size="sm"
                    className="h-9 min-w-9 rounded-full"
                    onClick={() => setFilters((prev) => ({ ...prev, page }))}
                  >
                    {page}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              className={dashboardStyles.paginationBtn}
              disabled={pagination.currentPage >= pagination.pages}
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  page: Math.min(pagination.pages, prev.page + 1),
                }))
              }
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Order Card Component for Mobile View
const OrderCard = ({
  order,
  getStatusColor,
  getPaymentStatusColor,
  formatCurrency,
}: {
  order: Order;
  onView: (orderId: string) => void;
  getStatusColor: (status: string) => string;
  getPaymentStatusColor: (status: string) => string;
  formatCurrency: (amount: number, currency?: string) => string;
}) => {
  return (
    <div className={dashboardStyles.listItem}>
      <div className="flex-1 min-w-0 w-full">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 truncate text-sm">
                {order.orderNumber}
              </h3>
              <p className="text-xs text-gray-500 truncate">
                {order.customer.name} • {order.customer.email}
              </p>
            </div>

            <Link
              to={`/dashboard/orders/${order._id}`}
              className="text-primary hover:underline text-sm"
            >
              View
            </Link>
          </div>

          {/* Order Stats */}
          <div className="flex items-center gap-2 mb-2">
            <Badge className={`text-xs ${getStatusColor(order.status)}`}>
              {order.status}
            </Badge>
            <Badge
              className={`text-xs ${getPaymentStatusColor(
                order.payment.status
              )}`}
            >
             Payment {order.payment.status}
            </Badge>
          </div>

          {/* Additional Info */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>
              {order.items.length} item{order.items.length !== 1 ? "s" : ""}
            </span>
            <span className="font-medium text-base font-heading text-black">
              {formatCurrency(order.pricing.total, order.pricing.currency)}
            </span>
          </div>

          {/* Date */}
          <div className="text-xs text-gray-400 mt-1">
            {format(new Date(order.createdAt), "MMM d, yyyy • h:mm a")}
          </div>
        </div>
    </div>
  );
};
